import * as React from 'react';
import { Box } from '@drone-js/core';

import { withBackground } from '@v2/pages/hoc/withBackground';
import { withNavHeader } from '@v2/contexts/navigation/hoc/withNavHeader';
import { withThemeProvider } from './withThemeProvider';
import { useIsMobile } from '@contexts/app/hooks/useIsMobile';
import { BackgroundType, PaddingType } from '@drone-js/theme';
import { usePageBackground } from '../hooks/usePageBackground';
import { useAppContext } from '@contexts/app/hooks/useAppContext';

export type PageBackgroundContext = {
    setBackgroundColor: (props: BackgroundType) => void;
    backgroundColor: BackgroundType;
};

export const PageBackgroundContext = React.createContext<PageBackgroundContext>({
    setBackgroundColor: () => {},
    backgroundColor: undefined,
});

export function withPageBackground<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const WrappedComponent: React.FC<React.PropsWithRef<TProps>> = props => {
        const [pageBackground, setPageBackground] = React.useState<BackgroundType | undefined>(undefined);
        return (
            <PageBackgroundContext.Provider
                value={{ setBackgroundColor: setPageBackground, backgroundColor: pageBackground }}
            >
                <MemoizedComponent {...props} />
            </PageBackgroundContext.Provider>
        );
    };

    return WrappedComponent;
}

export function withWrapper<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const WrappedComponent: React.FC<React.PropsWithRef<TProps>> = props => {
        const { backgroundColor } = usePageBackground();
        const { initialPath } = useAppContext();
        const isWallet = initialPath?.includes('wallet');
        const padding: PaddingType = useIsMobile() ? (isWallet ? '8' : '16') : '32';
        return (
            <Box p={padding} backgroundColor={backgroundColor}>
                <MemoizedComponent {...props} />
            </Box>
        );
    };

    return WrappedComponent;
}

export const withPageBasic = (fc: React.FC<unknown>) =>
    withThemeProvider(withPageBackground(withBackground(withNavHeader(withWrapper(fc)))));
